/***
*
*   safeRedirectUrl / followSafeRedirect
*   The guard for a server-sent redirect before a public view navigates to it.
*
*   The public form views assign response.data.redirect to
*   window.location.href. The redirect comes from a firm's onboarding settings,
*   so a javascript: URL there would run script in this page's origin. Only an
*   absolute http: or https: URL is followed; anything else (another scheme, a
*   protocol-relative //host, or a value with a control
*   character the URL parser would silently drop) is ignored. A same-origin
*   path (one leading "/" not followed by "/" or "\", such as "/finish") is
*   followed too: it cannot leave this origin or run script.
*
*   The server applies the same guard (helper/safeRedirectUrl.js). This copy
*   protects the page even if a response still carries an unsafe value.
*
*   followAbortRedirect applies it to the form schema's abort rule too: the
*   firm writes abort.redirect_url into the form JSON, so it is no safer than
*   a server-sent redirect.
*
*   Pure, like resend-gate.js, so it is testable without mounting a view.
*
**********/

const ALLOWED_PROTOCOLS = ["http:", "https:"];

// C0 controls (U+0000-U+001F) and DEL (U+007F).
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;

// A path on this origin: one "/" first, and not "//" or "/\" (which browsers
// resolve to another host).
const SAME_ORIGIN_PATH = /^\/(?![/\\])/;

// The value, trimmed, when it is an absolute http(s) URL or a same-origin
// path; otherwise null.
export function safeRedirectUrl(value) {
  if (typeof value !== "string") return null;

  const candidate = value.trim();
  if (!candidate || CONTROL_CHARACTER.test(candidate)) return null;
  if (SAME_ORIGIN_PATH.test(candidate)) return candidate;

  let parsed;
  try {
    // No base URL: a relative or protocol-relative value throws.
    parsed = new URL(candidate);
  } catch (e) {
    return null;
  }

  return ALLOWED_PROTOCOLS.includes(parsed.protocol) ? candidate : null;
}

// Navigates to the redirect only when it is safe. Returns whether it did.
export function followSafeRedirect(value, location = window.location) {
  const target = safeRedirectUrl(value);
  if (!target) return false;
  location.href = target;
  return true;
}

// The fallback when an abort rule has no safe redirect of its own.
const ABORT_FALLBACK = "/finish";

// The form schema's abort rule: when the answer to abort.name is exactly true
// and abort.type is "redirect", the page leaves without submitting. It goes to
// abort.redirect_url only when that is a safe redirect, else to /finish.
// Returns whether the rule fired (the caller then stops the submission).
export function followAbortRedirect(abort, answers, location = window.location) {
  if (!abort || abort.type !== "redirect" || answers?.[abort.name] !== true) {
    return false;
  }
  if (!followSafeRedirect(abort.redirect_url, location)) {
    location.href = ABORT_FALLBACK;
  }
  return true;
}
